/**
 * @module contracts/manager
 */

/**
 * @typedef {module:contracts/contract~Contract} Contract
 * @typedef {module:utils/object~Model} Model
 * @typedef {Model} Manager
 */
import attempt from '../utils/attempt.js'
import inherits from '../utils/inherits.js'
import create, { prototype, selectors } from './contract.js'
import scope from '../selectors/scope.js'
import all from '../selectors/all.js'
import object from '../utils/object.js'
import resolvable from '../utils/resolvable.js'
import append from '../tree/append.js'
import body from '../tree/body.js'
import { tick } from '../tree/removed.js'
import xpath from '../selectors/xpath.js'
import updaters from './updaters.js'

const deferred = 'descendant::*[@*[starts-with(name(), "data-deferred-")]]'

const trees = [
  ':scope > [data-after]',
  ':scope > [data-append]',
  ':scope > [data-empty]',
  ':scope > [data-before]',
  ':scope > [data-prepend]',
  ':scope > [data-replace]'
]

const { isArray } = Array

const { assign, entries, freeze, isFrozen } = Object

const TYPE_CONTRACT_MANAGER = true

const store = new WeakMap()

const resolved = new WeakMap()

const states = []

const log = error => queueMicrotask(() => {
  throw freeze(assign(error, { TYPE_CONTRACT_MANAGER }))
})

const call = async (
  url,
  element,
  values,
  template = null
) => {
  if (!resolved.has(element)) {
    resolved.set(element, [])
  }

  const current = resolved.get(element)

  for (const value of values) {
    const task = await resolve(url, value)

    if (!current.includes(task)) {
      await task(element, template)
      current.push(task)
    }
  }
}

const isContract = (
  value
) => {
  return isFrozen(value)
    && (inherits(value, prototype) || isArray(value))
}

const triggerContract = async (
  url,
  target,
  contract,
  template = null
) => {
  if (!isArray(contract)) {
    const { dataset } = target
    const { contract: name } = dataset

    throw new TypeError(`A named contract must be an array: "${name}"`)
  }

  for (const task of contract) {
    await call(url, target, contract, template)
  }
}

const triggerDeferred = async (
  url,
  target,
  contracts,
  template
) => {
  for (const contract of contracts) {
    for (const [selector, tasks] of entries(contract[selectors] ?? {})) {
      if (!selector.startsWith('[data-deferred-')) {
        continue
      }

      if (target.matches(selector)) {
        await attempt(call(url, target, tasks, template))
        continue
      }

      for (const element of all(selector, target)) {
        await attempt(call(url, element, tasks, template))
      }
    }
  }
}

const triggerSelectors = async (
  url,
  target,
  contracts
) => {
  for (const contract of contracts) {
    for (const [selector, tasks] of entries(contract[selectors] ?? {})) {
      if (selector.startsWith('[data-deferred-')) {
        continue
      }

      if (selector === scope) {
        await attempt(call(url, target, tasks))
        continue
      }

      for (const element of all(selector, target)) {
        await attempt(call(url, element, tasks))
      }
    }
  }
}

const parse = (
  contract,
  name
) => {
  const contracts = []
  let current = contract

  for (const segment of name.split('.')) {
    current = current[segment]

    if (!isContract(current)) {
      throw new TypeError(`Unknown contract "${name}"`)
    }

    if (!isFrozen(current)) {
      throw new TypeError(`A contract must be frozen`)
    }

    contracts.unshift(current)
  }

  return contracts
}

const resolve = (
  url,
  value
) => {
  if (inherits(value, url)) {
    /* @todo lazy-import
    current = (await import(current)).default
    type = typeof current
    */
  }

  return value
}

const trigger = async (target, template) => {
  const { ownerDocument } = target
  const { defaultView } = ownerDocument
  const { URL } = defaultView
  const { prototype } = URL
  const { contract, tree } = store.get(defaultView)
  
  await attempt(triggerSelectors(prototype, target, [contract], template))
  
  for (const element of all('[data-contract]', target)) {
    try {
      const { dataset } = element
      const { contract: names } = dataset
      
      for (const name of names.split(' ')) {
        const [first] = parse(contract, name)
        
        await attempt(triggerContract(prototype, element, first, template))
      }
    } catch (error) {
      log(error)
    }
  }
  
  for (const element of all('[data-fragment]', target)) {
    try {
      const { dataset } = element
      const { fragment: names } = dataset
      
      for (const name of names.split(' ')) {
        const contracts = parse(contract, name)
        
        await attempt(triggerSelectors(prototype, element, contracts, template))
      }
    } catch (error) {
      log(error)
    }
  }
  
  await attempt(triggerSelectors(prototype, target, [tree], template))
}

/**
 * The contracts manager
 * @type {Manager}
 */
export default object(null, {
  /**
   * Registers a contract for a specific window
   * @param {Window} window
   * @param {Contract} contract
   * @returns {Promise<void>}
   */
  async register (
    window,
    contract
  ) {
    if (!store.has(window)) {
      const { document } = window
      const tasks = {}
      const tree = {}

      for (const [selector, fn] of Object.entries(contract[selectors])) {
        const target = trees.includes(selector) ? tree : tasks

        target[selector] = fn
      }

      store.set(window, object(null, {
        contract: create({
          ...contract,
          [selectors]: tasks
        }),
        tree: create({
          ...contract,
          [selectors]: tree
        })
      }))

      return this.trigger(document.body)
    }
  },
  /**
   * Gets the promise of the current triggering
   * @returns {?Promise<void>}
   */
  get current () {
    return states.at(0)
  },
  /**
   * Awaits the current processes end
   * @returns {Promise<void>}
   */
  async ready () {
    return Promise.all(states)
  },
  /**
   * Triggers the contracts on a provided node (<body>)
   * @param {Node} target
   * @param {?Node} template
   * @returns {Promise<void>}
   */
  async trigger (
    target,
    template = null
  ) {
    const { ownerDocument } = target
    const { defaultView } = ownerDocument
    const { URL } = defaultView
    const { prototype } = URL
    const [state, { resolve, reject }] = resolvable()
    const previous = states.at(-1)
    const { contract } = store.get(defaultView)

    try {
      states.push(state)
      tick(state)
      await previous
      const updates = updaters(target)
      const matches = [
        ...xpath(deferred, updates.target),
        ...xpath(deferred, target)
      ]
      
      await trigger(target, template)
      await trigger(updates.target, template)
      updates.update()
      
      for (const element of matches) {
        try {
          await attempt(triggerDeferred(prototype, element, [contract], template))
        } catch (error) {
          log(error)
        }
      }

      resolve()
    } catch (error) {
      reject(error)
      throw error
    } finally {
      states.shift()
    }
  },
  /**
   * Triggers the contracts on any provided nodes
   * @param {...Node[]} targets
   * @returns {Promise<void>}
   */
  async fragment (
    ...targets
  ) {
    const wrapper = body()

    append(wrapper, targets)
    await this.trigger(wrapper)
  }
})
